import React from 'react';

import BlockContent, {BlockContentProps} from '@sanity/block-content-to-react';
import imageUrlBuilder from '@sanity/image-url';
import css from '@styled-system/css';
import {EmailBanner} from 'src/components/editorial/EmailBanner';
import {LabelledImage, EditorialContent} from 'src/components/shared';
import {ResponsiveContentWrapper} from 'src/components/shared/ResponsiveContentWrapper';
import {Typography} from 'src/components/shared/typography';
import {colors} from 'src/styleguide/colors';
import {fonts} from 'src/styleguide/typographyStyles';
import {sanityClient} from 'src/utils/sanity';
import styled from 'styled-components/macro';

import {BodyCallOutText, Factoid, IconContent, ChiefHighlightsButtons} from '../../components/editorial';
import {PullQuoteImage} from './PullQuoteImage';
import {EditorialQuote} from './Quote';

export type RichContentType = typeof BlockContent;

const primaryTextAttributes = {
  mb: '2rem',
  fontSize: '1.125rem',
  lineHeight: '1.5rem',
  fontFamily: fonts.primary,
  color: colors.washingtonPostFontGray,
};

const EditorialBodyText = styled(Typography.P4).attrs(primaryTextAttributes)``;

const EditorialLink = styled(`a`).attrs(primaryTextAttributes)`
  color: ${({theme}) => theme.colors.darkGold};
  &:visited {
    color: ${({theme}) => theme.colors.darkGold};
  }
`;

const EditorialWistiaVideo = styled('iframe')`
  width: 100%;
  height: 100%;
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ListComponent: any = (props: any) =>
  props.type === 'number' ? <ol className="prose">{props.children}</ol> : <ul className="prose">{props.children}</ul>;

type BlockComponentStyles = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span' | 'normal' | 'blockquote';
export type SerializerProps = {
  children: JSX.Element;
  node: {
    style: BlockComponentStyles;
  };
};

const serializers: BlockContentProps['serializers'] = {
  marks: {
    link: ({mark, children}) => (
      <EditorialLink href={mark.href} target="_blank" rel="noreferrer noopener">
        {children}
      </EditorialLink>
    ),
  },
  list: ListComponent,
  types: {
    divider: () => {
      return <hr className="border-light-gold my-8" />;
    },
    quote: ({node}) => {
      return <EditorialQuote {...node} />;
    },
    bodyCallOutText: ({node}) => {
      return <BodyCallOutText {...node} />;
    },
    labelledImage: ({node}) => {
      return <LabelledImage {...node} />;
    },
    wistiaVideo: ({node}) => (
      <ResponsiveContentWrapper aspectRatio="16:9">
        <EditorialWistiaVideo src={`https://fast.wistia.net/embed/iframe/${node.wistiaVideoId}`} />
      </ResponsiveContentWrapper>
    ),
    block: ({children, node}) => {
      switch (node.style) {
        case 'heading1':
          return <h1>{children}</h1>;
        case 'heading2':
          return <h2>{children}</h2>;
        case 'heading3':
          return <h3>{children}</h3>;
        case 'heading4':
          return <h4>{children}</h4>;
        default:
          return <p className="type-p !text-white  not-prose">{children}</p>;
      }
    },
    factoid: ({node}) => {
      return <Factoid {...node} />;
    },

    pullQuoteImage: ({node}) => {
      return <PullQuoteImage {...node} />;
    },
    iconContent: ({node}) => {
      return <IconContent {...node} />;
    },
    chiefHighlightsButtons: ({node}) => {
      return <ChiefHighlightsButtons {...node} />;
    },
    emailSignUp: ({node}) => {
      return <EmailBanner id="newsletter-form" {...node} />;
    },
  },
};

export const NextUpChiefHighlights: RichContentType = props => (
  <EditorialContent serializers={serializers} blocks={props.blocks} />
);
